import {
  ExpandableRowDef,
  ExpandableTable,
} from '@/components/ExpandableTable/ExpandableTable';
import { ColumnDef } from '@/components/Table/types';
import useGetRequestActivities from '@/modules/Requests/queries/get-request-activities';
import { convertTimeSecondsToString } from '@/utils';
import { t } from 'i18next';
import { useMemo } from 'react';

type RowInfo = {
  title: string;
  estimatedSeconds: number;
  billableSeconds: number;
  nonBillableSeconds: number;
  totalSeconds: number;
};

type RequestActivityProps = {
  requestId: number;
};

export const RequestActivity = ({ requestId }: RequestActivityProps) => {
  const { data } = useGetRequestActivities({ id: requestId });
  const columns = useMemo<ColumnDef<RowInfo>[]>(
    () => [
      {
        accessor: 'title',
        id: 'title',
        name: t('activity_title_column_label'),
        alignment: 'left',
        tdClassNames: 'font-bold',
      },
      {
        accessor: 'estimatedSeconds',
        id: 'estimatedSeconds',
        name: t('activity_estimated_column_label'),
        renderRow: (row) => convertTimeSecondsToString(row.estimatedSeconds),
        renderFooterRow: () => '-',
      },
      {
        accessor: 'billableSeconds',
        id: 'billableSeconds',
        name: t('activity_billable_column_label'),
        renderRow: (row) => convertTimeSecondsToString(row.billableSeconds),
      },
      {
        accessor: 'nonBillableSeconds',
        id: 'nonBillableSeconds',
        name: t('activity_nonBillable_column_label'),
        renderRow: (row) => convertTimeSecondsToString(row.nonBillableSeconds),
      },
      {
        accessor: 'totalSeconds',
        id: 'totalSeconds',
        name: t('activity_total_column_label'),
        renderRow: (row) => convertTimeSecondsToString(row.totalSeconds),
      },
    ],
    [],
  );

  const rows = useMemo<ExpandableRowDef<RowInfo>[]>(() => {
    if (!data) {
      return [];
    }

    return data.map<ExpandableRowDef<RowInfo>>((activityCategory) => {
      return {
        id: activityCategory.category,
        billableSeconds: activityCategory.billableSeconds,
        estimatedSeconds: activityCategory.estimatedSeconds,
        nonBillableSeconds: activityCategory.nonBillableSeconds,
        title: t(`activity_category.${activityCategory.category}`),
        totalSeconds: activityCategory.totalSeconds,
        rows: activityCategory.activities.map<ExpandableRowDef<RowInfo>>(
          (activity) => {
            return {
              id: activity.type,
              title: t(`activity_type.${activity.type}`),
              billableSeconds: activity.billableSeconds,
              estimatedSeconds: activity.estimatedSeconds,
              nonBillableSeconds: activity.nonBillableSeconds,
              totalSeconds: activity.totalSeconds,
            };
          },
        ),
      };
    });
  }, [data]);

  const footerRow = useMemo<ExpandableRowDef<RowInfo>>(() => {
    const {
      billableSeconds,
      estimatedSeconds,
      nonBillableSeconds,
      totalSeconds,
    } = rows.reduce(
      (acc, row) => {
        return {
          billableSeconds: acc.billableSeconds + row.billableSeconds,
          estimatedSeconds: acc.estimatedSeconds + row.estimatedSeconds,
          nonBillableSeconds: acc.nonBillableSeconds + row.nonBillableSeconds,
          totalSeconds: acc.totalSeconds + row.totalSeconds,
        };
      },
      {
        billableSeconds: 0,
        estimatedSeconds: 0,
        nonBillableSeconds: 0,
        totalSeconds: 0,
      },
    );
    return {
      id: 'total',
      title: t('total'),
      billableSeconds,
      estimatedSeconds,
      nonBillableSeconds,
      totalSeconds,
    };
  }, [rows]);

  return (
    <ExpandableTable
      columns={columns}
      rows={rows}
      footerRow={footerRow}
      emptyStateMessage={t('table_empty_data')}
      footerRowClassName="text-blue-500 font-bold"
    />
  );
};
